/*
  File: CustomerDetailPage.tsx
  Path: src/features/dashboard/pages/CustomerDetailPage.tsx
  Description:
    AQUORIX Customers Surface Phase 3.
    Full-page Customer Detail consuming GET /api/v1/dashboard/customers/:guest_id.
    No AQX-C or visible customer ID. Current-schema fields only.

  Version: 0.1.0
  Status: Phase 3 Gate 3 Candidate
*/

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { apiFetch } from "../../../utils/api";
import "./CustomerDetailPage.css";

type TabKey = "profile" | "bookings" | "groups" | "dive_log" | "notes";

type CustomerDetailResponse = {
  ok?: boolean;
  status?: string;
  data?: {
    profile?: any;
    bookings?: any[];
    groups?: any[];
    dive_log?: any[];
    notes?: any[];
  };
  error?: string;
  message?: string;
};

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; data: CustomerDetailResponse };

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "profile", label: "Profile" },
  { key: "bookings", label: "Bookings" },
  { key: "groups", label: "Groups" },
  { key: "dive_log", label: "Dive Log" },
  { key: "notes", label: "Notes" }
];

function displayName(profile: any): string {
  return (
    profile?.name?.display_name ||
    `${profile?.name?.first_name || ""} ${profile?.name?.last_name || ""}`.trim() ||
    "Unnamed customer"
  );
}

function fullName(profile: any): string {
  return `${profile?.name?.first_name || ""} ${profile?.name?.last_name || ""}`.trim() || "Unnamed customer";
}

function initials(name: string): string {
  return (
    name
      .split(" ")
      .filter(Boolean)
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "CU"
  );
}

function labelize(value?: string | null): string {
  if (!value) return "—";
  return String(value).replace(/_/g, " ");
}

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function statusClass(status?: string | null): string {
  const normalized = String(status || "new").toLowerCase();
  if (normalized === "active") return "customer-detail-status customer-detail-status-active";
  if (normalized === "future_booking") return "customer-detail-status customer-detail-status-future";
  if (normalized === "lapsed") return "customer-detail-status customer-detail-status-lapsed";
  return "customer-detail-status customer-detail-status-new";
}

function money(row: any): string {
  const currency = row?.payment_currency || "JOD";
  const amount = row?.payment_amount || "—";
  return `${currency} ${amount}`;
}

function sessionLabel(row: any): string {
  const d = new Date(row?.dive_datetime || "");
  const time = Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${time} · ${labelize(row?.session_type)}`;
}

function bookingStatusClass(status?: string | null): string {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "confirmed" || normalized === "completed") return "statuscell s-conf";
  if (normalized === "pending") return "statuscell s-pend";
  return "statuscell s-pend";
}

function paymentStatusClass(status?: string | null): string {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "paid") return "statuscell s-paid";
  return "statuscell s-unpaid";
}

export default function CustomerDetailPage() {
  const nav = useNavigate();
  const { guest_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = String(searchParams.get("tab") || "profile") as TabKey;
  const activeTab: TabKey = TABS.some((tab) => tab.key === requestedTab) ? requestedTab : "profile";

  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [newGroupName, setNewGroupName] = useState("");
  const [groupSaving, setGroupSaving] = useState(false);
  const [groupMessage, setGroupMessage] = useState("");
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteMessage, setNoteMessage] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFullName, setEditFullName] = useState("");
  const [editPreferredName, setEditPreferredName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editMessage, setEditMessage] = useState("");
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [certModalOpen, setCertModalOpen] = useState(false);
  const [certAgency, setCertAgency] = useState("");
  const [certLevel, setCertLevel] = useState("");
  const [certNumber, setCertNumber] = useState("");
  const [certStatus, setCertStatus] = useState("not_recorded");
  const [certSaving, setCertSaving] = useState(false);
  const [certMessage, setCertMessage] = useState("");

  const [medicalModalOpen, setMedicalModalOpen] = useState(false);
  const [medicalStatus, setMedicalStatus] = useState("not_collected");
  const [readinessNote, setReadinessNote] = useState("");
  const [customerStatus, setCustomerStatus] = useState("active");
  const [medicalSaving, setMedicalSaving] = useState(false);
  const [medicalMessage, setMedicalMessage] = useState("");

  useEffect(() => {
    let mounted = true;

    async function loadCustomer() {
      if (!guest_id) {
        setState({ status: "error", message: "Missing customer route key." });
        return;
      }

      setState({ status: "loading" });

      try {
        const res = await apiFetch(`/api/v1/dashboard/customers/${encodeURIComponent(guest_id)}`, {
          method: "GET"
        });
        const json = (await res.json()) as CustomerDetailResponse;

        if (!res.ok || json.ok === false) {
          throw new Error(json.error || json.message || `Customer detail load failed (${res.status})`);
        }

        if (mounted) setState({ status: "ready", data: json });
      } catch (err: any) {
        if (mounted) setState({ status: "error", message: err?.message || "Customer detail load failed" });
      }
    }

    void loadCustomer();

    return () => {
      mounted = false;
    };
  }, [guest_id]);

  const data = state.status === "ready" ? state.data.data || {} : {};
  const profile = data.profile || {};
  const bookings = Array.isArray(data.bookings) ? data.bookings : [];
  const groups = Array.isArray(data.groups) ? data.groups : [];
  const diveLog = Array.isArray(data.dive_log) ? data.dive_log : [];
  const notes = Array.isArray(data.notes) ? data.notes : [];
  const now = new Date();
  const upcomingBookings = bookings.filter((booking) => {
    const d = new Date(booking?.dive_datetime || "");
    return !Number.isNaN(d.getTime()) && d >= now;
  });
  const pastBookings = bookings.filter((booking) => {
    const d = new Date(booking?.dive_datetime || "");
    return Number.isNaN(d.getTime()) || d < now;
  });

  const counts = useMemo(
    () => ({
      profile: 1,
      bookings: bookings.length,
      groups: groups.length,
      dive_log: diveLog.length,
      notes: Array.isArray(notes) ? notes.length : 0
    }),
    [bookings.length, groups.length, diveLog.length, notes]
  );

  function switchTab(tab: TabKey) {
    setSearchParams({ tab });
  }

  function closeGroupModal() {
    setGroupModalOpen(false);
    setSelectedGroupId("");
    setNewGroupName("");
    setGroupSaving(false);
    setGroupMessage("");
  }

  function closeNoteModal() {
    setNoteModalOpen(false);
    setNoteBody("");
    setNoteSaving(false);
    setNoteMessage("");
  }

  function openEditModal() {
    setEditFullName(`${profile.name?.first_name || ""} ${profile.name?.last_name || ""}`.trim());
    setEditPreferredName(profile.name?.preferred_name || "");
    setEditPhone(profile.contact?.phone || "");
    setEditEmail(profile.contact?.email || "");
    setEditMessage("");
    setEditSaving(false);
    setEditModalOpen(true);
  }

  function closeEditModal() {
    setEditModalOpen(false);
    setEditSaving(false);
    setEditMessage("");
  }

  function openCertModal() {
    const operatorProfile = profile.operator_profile || {};
    const summary = profile.certification_summary || {};
    setCertAgency(operatorProfile.certification_agency || summary.agency || "");
    setCertLevel(operatorProfile.certification_level || summary.level || "");
    setCertNumber(operatorProfile.certification_number || summary.number || "");
    setCertStatus(operatorProfile.certification_status || summary.status || "not_recorded");
    setCertMessage("");
    setCertSaving(false);
    setCertModalOpen(true);
  }

  function closeCertModal() {
    setCertModalOpen(false);
    setCertSaving(false);
    setCertMessage("");
  }

  function openMedicalModal() {
    const operatorProfile = profile.operator_profile || {};
    setMedicalStatus(operatorProfile.medical_status || profile.medical_status || "not_collected");
    setReadinessNote(operatorProfile.readiness_note || "");
    setCustomerStatus(operatorProfile.customer_status || profile.status || "active");
    setMedicalMessage("");
    setMedicalSaving(false);
    setMedicalModalOpen(true);
  }

  function closeMedicalModal() {
    setMedicalModalOpen(false);
    setMedicalSaving(false);
    setMedicalMessage("");
  }

  function mergeOperatorProfile(operatorProfile: any) {
    if (!operatorProfile) return;

    setState((current) => {
      if (current.status !== "ready") return current;
      const currentData = current.data.data || {};
      const currentProfile = currentData.profile || {};

      return {
        status: "ready",
        data: {
          ...current.data,
          data: {
            ...currentData,
            profile: {
              ...currentProfile,
              operator_profile: operatorProfile,
              certification_summary: {
                ...(currentProfile.certification_summary || {}),
                agency: operatorProfile.certification_agency || null,
                level: operatorProfile.certification_level || null,
                number: operatorProfile.certification_number || null,
                status: operatorProfile.certification_status || "not_recorded"
              },
              medical_status: operatorProfile.medical_status || currentProfile.medical_status,
              status: operatorProfile.customer_status || currentProfile.status
            }
          }
        }
      };
    });
  }

  useEffect(() => {
    if (!groupModalOpen && !noteModalOpen && !editModalOpen && !certModalOpen && !medicalModalOpen) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        if (groupModalOpen) closeGroupModal();
        if (noteModalOpen) closeNoteModal();
        if (editModalOpen) closeEditModal();
        if (certModalOpen) closeCertModal();
        if (medicalModalOpen) closeMedicalModal();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [groupModalOpen, noteModalOpen, editModalOpen, certModalOpen, medicalModalOpen]);

  async function saveNoteModal() {
    if (!guest_id || noteSaving) return;

    const body = noteBody.trim();

    if (!body) {
      setNoteMessage("Note body is required");
      return;
    }

    setNoteSaving(true);
    setNoteMessage("");

    try {
      const res = await apiFetch(`/api/v1/dashboard/customers/${encodeURIComponent(guest_id)}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body })
      });
      const json = await res.json();

      if (!res.ok || json.ok === false) {
        throw new Error(json.message || "Note could not be saved");
      }

      const newNote = json.data?.note;
      if (newNote) {
        setState((current) => {
          if (current.status !== "ready") return current;
          const currentData = current.data.data || {};
          const existingNotes = Array.isArray(currentData.notes) ? currentData.notes : [];
          return {
            status: "ready",
            data: {
              ...current.data,
              data: {
                ...currentData,
                notes: [newNote, ...existingNotes]
              }
            }
          };
        });
      }

      closeNoteModal();
      switchTab("notes");
    } catch (err: any) {
      setNoteMessage(err?.message || "Note could not be saved");
      setNoteSaving(false);
    }
  }

  async function saveGroupModal() {
    if (!guest_id || groupSaving) return;

    setGroupSaving(true);
    setGroupMessage("");

    try {
      let groupId = selectedGroupId;

      if (newGroupName.trim()) {
        const createRes = await apiFetch("/api/v1/dashboard/customer-groups", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: newGroupName.trim() })
        });
        const createJson = await createRes.json();

        if (!createRes.ok || createJson.ok === false) {
          throw new Error(createJson.message || "Group could not be created");
        }

        groupId = String(createJson.data?.group?.group_id || "");
      }

      if (!groupId) {
        setGroupMessage("Select a group or create a new group.");
        setGroupSaving(false);
        return;
      }

      const addRes = await apiFetch(`/api/v1/dashboard/customer-groups/${encodeURIComponent(groupId)}/members`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ guest_id: Number(guest_id) })
      });
      const addJson = await addRes.json();

      if (addRes.status === 409) {
        setGroupMessage(addJson.message || "Customer is already in this group.");
        setGroupSaving(false);
        return;
      }

      if (!addRes.ok || addJson.ok === false) {
        throw new Error(addJson.message || "Customer could not be added to this group");
      }

      closeGroupModal();
      switchTab("groups");
    } catch (err: any) {
      setGroupMessage(err?.message || "Customer could not be added to this group");
      setGroupSaving(false);
    }
  }

  async function saveCertModal() {
    if (!guest_id || certSaving) return;

    setCertSaving(true);
    setCertMessage("");

    try {
      const res = await apiFetch(`/api/v1/dashboard/customers/${encodeURIComponent(guest_id)}/operator-profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          certification_agency: certAgency.trim() || null,
          certification_level: certLevel.trim() || null,
          certification_number: certNumber.trim() || null,
          certification_status: certStatus
        })
      });
      const json = await res.json();

      if (!res.ok || json.ok === false) {
        throw new Error(json.message || "Certification could not be saved");
      }

      mergeOperatorProfile(json.data?.operator_profile);
      closeCertModal();
    } catch (err: any) {
      setCertMessage(err?.message || "Certification could not be saved");
      setCertSaving(false);
    }
  }

  async function saveMedicalModal() {
    if (!guest_id || medicalSaving) return;

    setMedicalSaving(true);
    setMedicalMessage("");

    try {
      const res = await apiFetch(`/api/v1/dashboard/customers/${encodeURIComponent(guest_id)}/operator-profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          medical_status: medicalStatus,
          readiness_note: readinessNote.trim() || null,
          customer_status: customerStatus
        })
      });
      const json = await res.json();

      if (!res.ok || json.ok === false) {
        throw new Error(json.message || "Readiness could not be saved");
      }

      mergeOperatorProfile(json.data?.operator_profile);
      closeMedicalModal();
    } catch (err: any) {
      setMedicalMessage(err?.message || "Readiness could not be saved");
      setMedicalSaving(false);
    }
  }

  function openDeleteModal() {
    setDeleteMessage("");
    setDeleteModalOpen(true);
  }

  function closeDeleteModal() {
    if (deleteSaving) return;
    setDeleteMessage("");
    setDeleteModalOpen(false);
  }

  async function deleteCustomer() {
    if (!guest_id || deleteSaving) return;

    setDeleteSaving(true);
    setDeleteMessage("");

    try {
      const res = await apiFetch(`/api/v1/dashboard/customers/${encodeURIComponent(guest_id)}`, {
        method: "DELETE"
      });
      const json = await res.json();

      if (!res.ok || json.ok === false) {
        throw new Error(json.message || "Customer could not be deleted");
      }

      setDeleteModalOpen(false);
      nav("/dashboard/customers");
    } catch (err: any) {
      setDeleteMessage(err?.message || "Customer could not be deleted");
      setDeleteSaving(false);
    }
  }

  async function saveEditModal() {
    if (!guest_id || editSaving) return;

    setEditSaving(true);
    setEditMessage("");

    try {
      const res = await apiFetch(`/api/v1/dashboard/customers/${encodeURIComponent(guest_id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: editFullName.trim(),
          preferred_name: editPreferredName.trim() || null,
          phone: editPhone.trim() || null,
          email: editEmail.trim() || null
        })
      });
      const json = await res.json();

      if (!res.ok || json.ok === false) {
        throw new Error(json.message || "Profile could not be saved");
      }

      const updatedProfile = json.data?.customer;
      if (updatedProfile) {
        setState((current) => {
          if (current.status !== "ready") return current;
          const currentData = current.data.data || {};
          return {
            status: "ready",
            data: {
              ...current.data,
              data: {
                ...currentData,
                profile: {
                  ...currentData.profile,
                  name: updatedProfile.name,
                  contact: updatedProfile.contact,
                  updated_at: updatedProfile.updated_at
                }
              }
            }
          };
        });
      }

      closeEditModal();
    } catch (err: any) {
      setEditMessage(err?.message || "Profile could not be saved");
      setEditSaving(false);
    }
  }

  if (state.status === "loading") {
    return (
      <div className="customer-detail-page">
        <div className="customer-detail-state">Loading customer…</div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="customer-detail-page">
        <button className="customer-detail-back" onClick={() => nav("/dashboard/customers")}>Back to Customers</button>
        <div className="customer-detail-state customer-detail-state-error">{state.message}</div>
      </div>
    );
  }

  const name = displayName(profile);

  return (
    <div className="customer-detail-page">
      <button className="customer-detail-back" onClick={() => nav("/dashboard/customers")}>Back to Customers</button>

      <section className="hcard">
        <div className="hav">{initials(name)}</div>
        <div className="hinfo">
          <div className="hname">
            {name}
            <span className="pill active"><span className="dot"></span>{labelize(profile.status || "active")}</span>
          </div>
          <div className="hmeta">
            <span>{labelize(profile.certification_summary?.agency || profile.certification_summary?.level || "Certification not recorded")}</span>
            <span>·</span>
            <span>{profile.contact?.phone || "No phone recorded"}</span>
            <span className="primary">Primary operator</span>
          </div>
        </div>

      </section>

      <section className="customer-actions-panel" aria-label="Customer actions">
        <div className="customer-action-strip" aria-label="Customer actions">
          <button type="button" className="abtn" onClick={() => setGroupModalOpen(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6M22 11h-6"/></svg>
            Add to group
          </button>
          <button type="button" className="abtn" onClick={() => setNoteModalOpen(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
            Add note
          </button>
          <button type="button" className="abtn" onClick={openCertModal}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4"/><path d="M12 3l7 4v5c0 5-3 8-7 9-4-1-7-4-7-9V7z"/></svg>
            Update cert
          </button>
          <button type="button" className="abtn" onClick={openMedicalModal}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/></svg>
            Update medical
          </button>
          <button type="button" className="abtn primary-btn" onClick={openEditModal}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>
            Edit profile
          </button>
        </div>
      </section>

      <section className="tabs" aria-label="Customer detail tabs">
        {TABS.filter((tab) => tab.key !== "notes").map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={activeTab === tab.key ? "tab on" : "tab"}
            onClick={() => switchTab(tab.key)}
          >
            {tab.label}
            <span className="tcount">{counts[tab.key]}</span>
          </button>
        ))}
      </section>

      <section className="customer-detail-panel">
        {activeTab === "profile" && (
          <div className="pgrid">
            <article className="pcard">
              <h2>Identity and contact</h2>
              <div className="frow"><span className="k">Full name</span><span className="v">{fullName(profile)}</span></div>
              <div className="frow"><span className="k">Preferred name</span><span className="v">{profile.name?.preferred_name || "—"}</span></div>
              <div className="frow"><span className="k">Phone</span><span className="v mono">{profile.contact?.phone || "No phone recorded"}</span></div>
              <div className="frow"><span className="k">Email</span><span className="v">{profile.contact?.email || "No email recorded"}</span></div>
            </article>

            <article className="pcard">
              <h2>Certifications</h2>
              <div className="certrow">
                <span className="certbadge">{profile.certification_summary?.agency || "TBD"}</span>
                <span className="v">{profile.certification_summary?.level || "Not recorded"}</span>
              </div>
              <div className="frow"><span className="k">Status</span><span className="v">{labelize(profile.certification_summary?.status || "not_recorded")}</span></div>
              <div className="frow"><span className="k">Number</span><span className="v mono">{profile.certification_summary?.number || "—"}</span></div>
              <div className="nc-foot"><button type="button" className="abtn" onClick={openCertModal}>Update certification</button></div>
              <div className="certrow" style={{ display: "none" }}>
              </div>
            </article>

            <article className="pcard">
              <h2>Readiness and declarations</h2>
              {"medical_status" in profile ? (
                <div className="frow"><span className="k">Medical declaration</span><span className="badge-ok">{labelize(profile.medical_status)}</span></div>
              ) : null}
              <div className="frow"><span className="k">Customer status</span><span className="badge-ok">{labelize(profile.status)}</span></div>
              {profile.operator_profile?.readiness_note ? (
                <div className="frow"><span className="k">Readiness note</span><span className="v">{profile.operator_profile.readiness_note}</span></div>
              ) : null}
              <div className="nc-foot"><button type="button" className="abtn" onClick={openMedicalModal}>Update readiness</button></div>
              <p className="medical-lock">Medical content is never shown here — status only. Other AQUORIX operator activity remains count-only.</p>
            </article>

            <article className="pcard">
              <h2>Operator activity</h2>
              <div className="frow"><span className="k">Bookings here</span><span className="v mono">{profile.booking_count ?? 0}</span></div>
              <div className="frow"><span className="k">Completed dives here</span><span className="v mono">{profile.completed_dive_count ?? 0}</span></div>
              <div className="frow"><span className="k">Other AQUORIX operators</span><span className="v mono">{profile.other_operator_activity_count ?? 0}</span></div>
              <div className="frow"><span className="k">Last dive</span><span className="v">{formatDateTime(profile.last_dive_at)}</span></div>
            </article>
          </div>
        )}

        {activeTab === "bookings" && (
          <div>
            <div className="sublabel">Upcoming</div>
            <div className="tcard future-band" style={{ marginBottom: "16px" }}>
              <table>
                <thead><tr><th>Date</th><th>Session</th><th>Dive site</th><th>Status</th><th>Total</th><th>Payment</th></tr></thead>
                <tbody>
                  {upcomingBookings.length ? upcomingBookings.map((booking) => (
                    <tr key={booking.booking_id}>
                      <td>{formatDate(booking.dive_datetime)}</td>
                      <td>{sessionLabel(booking)}</td>
                      <td>{booking.dive_site_name || "Site not recorded"}</td>
                      <td><span className={bookingStatusClass(booking.booking_status)}>{labelize(booking.booking_status)}</span></td>
                      <td className="money">{money(booking)}</td>
                      <td><span className={paymentStatusClass(booking.payment_status)}>{labelize(booking.payment_status)}</span></td>
                    </tr>
                  )) : <tr><td colSpan={6}>No upcoming bookings returned for this operator.</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="sublabel">Past</div>
            <div className="tcard">
              <table>
                <thead><tr><th>Date</th><th>Session</th><th>Dive site</th><th>Status</th><th>Total</th><th>Payment</th></tr></thead>
                <tbody>
                  {pastBookings.length ? pastBookings.map((booking) => (
                    <tr key={booking.booking_id}>
                      <td>{formatDate(booking.dive_datetime)}</td>
                      <td>{sessionLabel(booking)}</td>
                      <td>{booking.dive_site_name || "Site not recorded"}</td>
                      <td><span className={bookingStatusClass(booking.booking_status)}>{labelize(booking.booking_status)}</span></td>
                      <td className="money">{money(booking)}</td>
                      <td><span className={paymentStatusClass(booking.payment_status)}>{labelize(booking.payment_status)}</span></td>
                    </tr>
                  )) : <tr><td colSpan={6}>No past bookings returned for this operator.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "groups" && (
          groups.length ? (
            <div className="ggrid">
              {groups.map((group, index) => (
                <div className="gcard" key={group.group_id || group.name || index}>
                  <h3>{group.name || group.group_name || "Group"}</h3>
                  <p>{group.member_count ?? (Array.isArray(group.members) ? group.members.length : 0)} members · created {formatDate(group.created_at)}</p>
                  <div className="mavatars">
                    {Array.isArray(group.members) ? group.members.map((member: any, memberIndex: number) => (
                      <span key={member.guest_id || memberIndex}>{member.initials || initials(member.display_name || "Member")}</span>
                    )) : null}
                  </div>
                  <span className="mlink">View group billing report →</span>
                </div>
              ))}
            </div>
          ) : <div className="customer-detail-empty">No group affiliations returned.</div>
        )}

        {activeTab === "dive_log" && (
          <div>
            {diveLog.length ? (
              <div className="tcard">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Site</th>
                      <th>Max Depth</th>
                      <th>Bottom Time</th>
                      <th>Gas</th>
                      <th>Signed Off</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diveLog.map((dive, index) => (
                      <tr key={dive.completion_id || `${dive.session_id}-${dive.attested_at}`}>
                        <td>{diveLog.length - index}</td>
                        <td>{formatDate(dive.dive_datetime)}</td>
                        <td>{dive.dive_site_name || "Site not recorded"}</td>
                        <td>{dive.max_depth ? `${dive.max_depth} m` : "—"}</td>
                        <td>—</td>
                        <td>{dive.gas || "Air"}</td>
                        <td>{dive.signed_off_by || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="customer-detail-empty">No dive log entries returned for this operator.</div>}
            <p className="privacy-foot"><b>The digital Nesima Dive Planner.</b> Showing this customer's logged dives at Blue Current Divers. Prior dives at other AQUORIX operators are counted but never detailed (cross-operator partition).</p>
          </div>
        )}

        {false && activeTab === "notes" && Array.isArray(notes) && (
          <div>
            <div className="notecomposer">
              <textarea placeholder="Add an operator note about this customer…" value={noteBody} onChange={(event) => { setNoteBody(event.target.value); setNoteMessage(""); }}></textarea>
              {noteMessage ? <div className="hint">{noteMessage}</div> : null}
              <div className="nc-foot"><button className="abtn primary-btn" onClick={saveNoteModal}>{noteSaving ? "Saving" : "Save note"}</button></div>
            </div>
            {notes.length ? notes.map((note: any, index: number) => (
              <div className="note-item" key={note.note_id || index}>
                <div className="nh"><b>{note.author_name || note.author_email || "Operator"}</b><span>{formatDateTime(note.created_at || note.updated_at)}</span></div>
                <div className="nb">{note.note || note.body || note.text || "Note"}</div>
              </div>
            )) : <div className="customer-detail-empty">No operator notes returned.</div>}
            <p className="privacy-foot"><b>Operator-only.</b> Notes are visible to your team, never to the consumer, and never shared cross-operator. Hidden entirely for counter staff (tier 4).</p>
          </div>
        )}
      </section>

      <div className={groupModalOpen || noteModalOpen || editModalOpen || certModalOpen || medicalModalOpen ? "scrim open" : "scrim"} onClick={() => {
        if (groupModalOpen) closeGroupModal();
        if (noteModalOpen) closeNoteModal();
        if (editModalOpen) closeEditModal();
        if (certModalOpen) closeCertModal();
        if (medicalModalOpen) closeMedicalModal();
      }}></div>

      <div className={groupModalOpen ? "modal mounted open" : "modal"} role="dialog" aria-modal="true" aria-label="Add to group">
        <div className="mh">
          <div className="mav"><svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6M22 11h-6"/></svg></div>
          <div><h2>Add to group</h2><p>{name}</p></div>
          <button className="x" onClick={closeGroupModal} aria-label="Close">×</button>
        </div>
        <div className="mb">
          <div className="mfld"><label>Add to an existing group</label>
            <div className="grouppick">
              {groups.length ? groups.map((group, index) => {
                const id = String(group.group_id || "");
                const label = group.name || group.group_name || "Group";
                return (
                  <span
                    key={id || `${label}-${index}`}
                    className={selectedGroupId === id ? "gopt sel" : "gopt"}
                    onClick={() => {
                      setSelectedGroupId(id);
                      setNewGroupName("");
                      setGroupMessage("");
                    }}
                  >
                    {label}
                  </span>
                );
              }) : <span className="hint">No operator groups returned.</span>}
            </div>
            {groupMessage ? <span className="hint">{groupMessage}</span> : null}
          </div>
          <div className="divider">or</div>
          <div className="mfld"><label>Create a new group</label><input type="text" placeholder="e.g. Al-Hashemi Family" value={newGroupName} onChange={(event) => { setNewGroupName(event.target.value); setSelectedGroupId(""); setGroupMessage(""); }} /><span className="hint">Creates the group and adds this customer to it.</span></div>
        </div>
        <div className="mf"><button className="cancel" onClick={closeGroupModal}>Cancel</button><button className="save" onClick={saveGroupModal}>{groupSaving ? "Saving" : "Save"}</button></div>
      </div>
      <div className={noteModalOpen ? "modal mounted open" : "modal"} role="dialog" aria-modal="true" aria-label="Add note">
        <div className="mh">
          <div className="mav"><svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></svg></div>
          <div><h2>Add note</h2><p>{name}</p></div>
          <button className="x" onClick={closeNoteModal} aria-label="Close">×</button>
        </div>
        <div className="mb">
          <div className="mfld"><label>Operator note</label><textarea placeholder="Add an operator note about this customer..." value={noteBody} onChange={(event) => { setNoteBody(event.target.value); setNoteMessage(""); }}></textarea><span className="hint">Operator-only. Notes are visible to your team, never to the consumer, and never shared cross-operator.</span></div>
          {noteMessage ? <div className="hint">{noteMessage}</div> : null}
        </div>
        <div className="mf"><button className="cancel" onClick={closeNoteModal}>Cancel</button><button className="save" onClick={saveNoteModal}>{noteSaving ? "Saving" : "Save note"}</button></div>
      </div>

      <div className={certModalOpen ? "modal mounted open" : "modal"} role="dialog" aria-modal="true" aria-label="Update certification">
        <div className="mh">
          <div className="mav">{initials(name)}</div>
          <div><h2>Update certification</h2><p>{name}</p></div>
          <button className="x" onClick={closeCertModal} aria-label="Close">×</button>
        </div>
        <div className="mb">
          <div className="mfld"><label>Agency</label><input type="text" placeholder="PADI, SSI, RAID..." value={certAgency} onChange={(event) => { setCertAgency(event.target.value); setCertMessage(""); }} /></div>
          <div className="mfld"><label>Level</label><input type="text" placeholder="Open Water Diver" value={certLevel} onChange={(event) => { setCertLevel(event.target.value); setCertMessage(""); }} /></div>
          <div className="mfld"><label>Certification number</label><input type="text" value={certNumber} onChange={(event) => { setCertNumber(event.target.value); setCertMessage(""); }} /></div>
          <div className="mfld"><label>Status</label>
            <select value={certStatus} onChange={(event) => { setCertStatus(event.target.value); setCertMessage(""); }}>
              <option value="not_recorded">Not recorded</option>
              <option value="claimed">Claimed</option>
              <option value="verified">Verified</option>
              <option value="needs_review">Needs review</option>
            </select>
            <span className="hint">Operator-scoped. This does not overwrite the global diver identity.</span>
          </div>
          {certMessage ? <div className="hint">{certMessage}</div> : null}
        </div>
        <div className="mf"><button className="cancel" onClick={closeCertModal}>Cancel</button><button className="save" onClick={saveCertModal}>{certSaving ? "Saving" : "Save certification"}</button></div>
      </div>

      <div className={medicalModalOpen ? "modal mounted open" : "modal"} role="dialog" aria-modal="true" aria-label="Update readiness">
        <div className="mh">
          <div className="mav">{initials(name)}</div>
          <div><h2>Update readiness</h2><p>{name}</p></div>
          <button className="x" onClick={closeMedicalModal} aria-label="Close">×</button>
        </div>
        <div className="mb">
          <div className="mfld"><label>Medical status</label>
            <select value={medicalStatus} onChange={(event) => { setMedicalStatus(event.target.value); setMedicalMessage(""); }}>
              <option value="not_collected">Not collected</option>
              <option value="no_conditions_declared">No conditions declared</option>
              <option value="declared_cleared_by_physician">Declared — cleared by physician</option>
              <option value="declared_awaiting_clearance">Declared — awaiting clearance</option>
              <option value="needs_review">Needs review</option>
            </select>
            <span className="hint">Status only. Medical detail is not stored here.</span>
          </div>
          <div className="mfld"><label>Customer status</label>
            <select value={customerStatus} onChange={(event) => { setCustomerStatus(event.target.value); setMedicalMessage(""); }}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="watch">Watch</option>
            </select>
          </div>
          <div className="mfld"><label>Readiness note</label><textarea placeholder="Optional operator-only readiness note..." value={readinessNote} onChange={(event) => { setReadinessNote(event.target.value); setMedicalMessage(""); }}></textarea></div>
          {medicalMessage ? <div className="hint">{medicalMessage}</div> : null}
        </div>
        <div className="mf"><button className="cancel" onClick={closeMedicalModal}>Cancel</button><button className="save" onClick={saveMedicalModal}>{medicalSaving ? "Saving" : "Save readiness"}</button></div>
      </div>

      <section className="customer-notes-panel" aria-label="Customer notes">
        <div className="customer-notes-head">
          <div>
            <h2>Notes</h2>
            <p>Operator notes stay visible here for quick context.</p>
          </div>
          <button type="button" className="abtn" onClick={() => setNoteModalOpen(true)}>Add note</button>
        </div>
        {Array.isArray(notes) && notes.length ? (
          <div className="customer-notes-list">
            {notes.map((note: any) => (
              <article key={note.note_id || note.created_at || note.body} className="customer-note-card">
                <p>{note.body}</p>
                <span>{note.created_at ? String(note.created_at) : ""}</span>
              </article>
            ))}
          </div>
        ) : (
          <div className="customer-notes-empty">
            No notes yet. Add the first note for this customer.
          </div>
        )}
      </section>

      <section className="customer-danger-zone" aria-label="Danger zone">
        <div>
          <h2>Danger zone</h2>
          <p>Customers with booking history cannot be deleted.</p>
        </div>
        <button type="button" className="abtn danger-btn" onClick={openDeleteModal}>Delete customer</button>
      </section>

      <div className={deleteModalOpen ? "modal mounted open customer-delete-modal" : "modal customer-delete-modal"} role="dialog" aria-modal="true" aria-label="Delete customer">
        <div className="modal-head">
          <div>
            <h2>Delete customer?</h2>
            <p>{name}</p>
          </div>
          <button type="button" className="modal-x" onClick={closeDeleteModal} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <div className="customer-delete-warning">
            This removes this customer from this operator. Customers with booking history cannot be deleted.
          </div>
          {deleteMessage ? <div className="customer-delete-message">{deleteMessage}</div> : null}
        </div>
        <div className="modal-foot">
          <button type="button" className="ghost-btn" onClick={closeDeleteModal} disabled={deleteSaving}>Cancel</button>
          <button type="button" className="danger-confirm-btn" onClick={deleteCustomer} disabled={deleteSaving}>
            {deleteSaving ? "Deleting..." : "Delete customer"}
          </button>
        </div>
      </div>

      <div className={editModalOpen ? "modal mounted open" : "modal"} role="dialog" aria-modal="true" aria-label="Edit profile">
        <div className="mh">
          <div className="mav">{initials(editFullName || name)}</div>
          <div><h2>Edit profile</h2><p>{name}</p></div>
          <button className="x" onClick={closeEditModal} aria-label="Close">×</button>
        </div>
        <div className="mb">
          <div className="mfld"><label>Full name</label><input type="text" value={editFullName} onChange={(event) => { setEditFullName(event.target.value); setEditMessage(""); }} /></div>
          <div className="mfld"><label>Preferred name</label><input type="text" value={editPreferredName} onChange={(event) => { setEditPreferredName(event.target.value); setEditMessage(""); }} /></div>
          <div className="mfld"><label>Phone</label><input type="text" value={editPhone} onChange={(event) => { setEditPhone(event.target.value); setEditMessage(""); }} /></div>
          <div className="mfld"><label>Email</label><input type="email" value={editEmail} onChange={(event) => { setEditEmail(event.target.value); setEditMessage(""); }} /></div>
          <span className="hint">Certifications and medical declarations are managed separately.</span>
          {editMessage ? <div className="hint">{editMessage}</div> : null}
        </div>
        <div className="mf"><button className="cancel" onClick={closeEditModal}>Cancel</button><button className="save" onClick={saveEditModal}>{editSaving ? "Saving" : "Save profile"}</button></div>
      </div>

    </div>
  );
}
